import { ButtonTertiary, ButtonPrimary, IconButton } from "@nova/component-btn"
import { formatBytes } from "@nova/format"
import { Status } from "@nova/ds-component-status"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import { useS3FileUrl } from "plugins/shared/s3url"
import { useState, useEffect, useRef } from "react"
import { useS3File } from "themes/default-tailwind/panels/GlobalUploadContainer"
import { downloadUri } from "tools/browser/download"
import {
    userCertificateS3Key,
    useDeleteUserCertificate,
} from "../user-certificates/UserCertificateQueries"
import {
    EditPilotCertificationModal,
    UploadPilotCertificationModal,
} from "./PilotCertificationModal"
import { Table } from "@nova/ds-component-table"
import * as Icons from "@nova/component-icons"
import { CertificateQueryResponse } from "./PilotQueries"
import { ExpandableContent } from "@nova/component-expandable-content"
import classNames from "classnames"
import { useAppDispatch } from "integrations/redux/hooks"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"
import { isFilenameForImage, isFilenameForPdf } from "@nova/util-strings"

export function PilotCertificates({
    pilotId,
    certificates,
    isLoading,
}: {
    pilotId: string
    certificates: CertificateQueryResponse
    isLoading?: boolean
}) {
    const confirmationModal = useConfirmationModalContext()
    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-between items-center">
                <h2 className="text-white text-[22px] telegraf">Certifications</h2>
                <ButtonTertiary
                    onClick={async () => {
                        await confirmationModal.confirm({
                            RenderComponent: ({ cancel, confirm }) => (
                                <UploadPilotCertificationModal
                                    userId={pilotId}
                                    onClose={cancel}
                                    onSubmit={confirm}
                                />
                            ),
                        })
                    }}
                    title="Add"
                    LeftIcon={Icons.Plus}
                />
            </div>
            <Table>
                <Table.Head>
                    <Table.Header>
                        <div>Name</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Type</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Certificate Number</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Date added</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Size</div>
                    </Table.Header>
                    <Table.Header>
                        <div>Expires</div>
                    </Table.Header>
                    {/* <Table.Header>Reminder</Table.Header> */}
                </Table.Head>
                <Table.Body>
                    {isLoading && (
                        <Table.Row>
                            <Table.Cell className="text-gray-400">Loading...</Table.Cell>
                        </Table.Row>
                    )}
                    {!certificates.length && (
                        <Table.Row>
                            <Table.Cell colSpan={6}>
                                <div className="flex flex-col items-center gap-1 py-8">
                                    <h3 className="text-white">No Certifications Uploaded</h3>
                                    <p className="text-gray-400 text-sm">
                                        Upload a certification for this pilot.
                                    </p>
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    )}
                    {!isLoading &&
                        !!certificates.length &&
                        certificates.map((c) => <CertificateRow userCertificate={c} key={c.id} />)}
                </Table.Body>
            </Table>
        </div>
    )
}

const CertificateRow = ({
    userCertificate,
}: {
    userCertificate: CertificateQueryResponse[number]
}) => {
    const dispatch = useAppDispatch()
    const [isOpen, setIsOpen] = useState(false)
    const expandedRowRef = useRef<HTMLTableRowElement>(null)
    useEffect(() => {
        if (isOpen) expandedRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }, [isOpen])

    const s3Key = userCertificateS3Key(
        userCertificate.org_id,
        userCertificate.user_id,
        userCertificate.id
    )

    const { data: url } = useS3FileUrl(s3Key)
    const { deleteUserCertificate } = useDeleteUserCertificate()

    const isPdf = isFilenameForPdf(userCertificate.file_name)

    const { data: fileBlob } = useS3File(s3Key, { disabled: !isOpen || !isPdf })

    const [displayUrl, setDisplayUrl] = useState<string>()
    useEffect(() => {
        if (!fileBlob) return
        const blob = new Blob([fileBlob], { type: "application/pdf" })
        const objectUrl = URL.createObjectURL(blob)
        setDisplayUrl(objectUrl)
        return () => URL.revokeObjectURL(objectUrl)
    }, [fileBlob])

    const handleDownload = () => {
        if (!url || !userCertificate.file_name) return
        downloadUri(userCertificate.file_name, url)
    }

    const confirmationModal = useConfirmationModalContext()

    const canPreview = isPdf || isFilenameForImage(userCertificate.file_name)
    const canExpand = canPreview || !!userCertificate.notes
    const isExpired =
        userCertificate.expires_at && userCertificate.expires_at.getTime() < Date.now()

    return (
        <>
            <Table.Row
                className={classNames("relative group/certRow", { "cursor-pointer": canExpand })}
                onClick={() => canExpand && setIsOpen((prev) => !prev)}
            >
                <Table.Cell className="flex items-center gap-2 h-full">
                    {canExpand ? (
                        <Icons.SmallIcon
                            Icon={isOpen ? Icons.ArrowDropdown : Icons.ArrowDropdownRight}
                        />
                    ) : (
                        <div className="w-5" />
                    )}
                    {userCertificate.name}
                </Table.Cell>
                <Table.Cell>
                    {userCertificate.file_name
                        ? (userCertificate.file_name.split(".").at(-1)?.toUpperCase() ?? "Unknown")
                        : "Manual"}
                </Table.Cell>
                <Table.Cell>{userCertificate.certificate_number}</Table.Cell>
                <Table.Cell>{userCertificate.created_at.toDateString?.()}</Table.Cell>
                <Table.Cell>
                    {userCertificate.file_size_bytes != null
                        ? formatBytes(userCertificate.file_size_bytes)
                        : null}
                </Table.Cell>
                <Table.Cell>
                    {isExpired !== null && (
                        <Status type={isExpired ? "error" : "positive"} variant="outline">
                            {userCertificate.expires_at!.toDateString()}
                        </Status>
                    )}
                </Table.Cell>
                {/* <Table.Cell>rmndr</Table.Cell> */}
                <Table.Cell
                    onClick={(e) => e.stopPropagation()}
                    className="absolute !p-0 h-full right-4 -top-0 flex items-center gap-4 opacity-0 group-hover/certRow:opacity-100 bg-[linear-gradient(90deg,rgba(18,18,22,0)_0%,#121216_16.35%)]"
                >
                    {userCertificate.file_name && (
                        <ButtonPrimary
                            title="Download"
                            LeftIcon={Icons.Download}
                            onClick={handleDownload}
                        />
                    )}
                    <IconButton
                        onClick={() => {
                            confirmationModal.confirm({
                                RenderComponent: ({ cancel, confirm }) => (
                                    <EditPilotCertificationModal
                                        certificate={userCertificate}
                                        onClose={cancel}
                                        onSubmit={confirm}
                                    />
                                ),
                            })
                        }}
                    >
                        <Icons.SmallIcon Icon={Icons.Edit} />
                    </IconButton>
                    <IconButton
                        onClick={() => {
                            confirmationModal.confirm({
                                RenderComponent: getGenericConfirmationModalComponent({
                                    title: "Delete Certificate",
                                    message: `Are you sure you want to delete ${userCertificate.name}?`,
                                }),
                                asyncConfirmCallback: async () => {
                                    try {
                                        await deleteUserCertificate(userCertificate.id)
                                        dispatch(
                                            setGlobalNotificationContext({
                                                message: `${userCertificate.name} has been removed.`,
                                                status: GlobalNotificationStatus.success,
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    } catch {
                                        dispatch(
                                            setGlobalNotificationContext({
                                                message: `Failed to remove ${userCertificate.name}. Please try again.`,
                                                status: GlobalNotificationStatus.error,
                                                timeoutInMs: 3000,
                                            })
                                        )
                                    }
                                },
                            })
                        }}
                    >
                        <Icons.SmallIcon Icon={Icons.Trash} />
                    </IconButton>
                </Table.Cell>
            </Table.Row>
            <Table.Row ref={expandedRowRef}>
                <Table.Cell className={classNames("!p-0", { "!border-b-0": !isOpen })} colSpan={6}>
                    <ExpandableContent isOpen={isOpen}>
                        <div className="flex items-start gap-6 px-10 py-4">
                            {canPreview && (
                                <div className="w-[300px] h-[150px] shrink-0 overflow-hidden">
                                    {isPdf ? (
                                        displayUrl ? (
                                            <embed
                                                src={`${displayUrl}#toolbar=0&navpanes=0`}
                                                type="application/pdf"
                                                className="w-full h-full"
                                            />
                                        ) : (
                                            <div className="w-full h-full animate-pulse bg-gray-700" />
                                        )
                                    ) : url ? (
                                        <img
                                            src={url}
                                            alt={userCertificate.file_name ?? ""}
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full animate-pulse bg-gray-700" />
                                    )}
                                </div>
                            )}
                            <h5 className="text-gray-100 max-w-[250px] whitespace-pre-wrap">
                                {userCertificate.notes}
                            </h5>
                        </div>
                    </ExpandableContent>
                </Table.Cell>
            </Table.Row>
        </>
    )
}
